<top-bar-filters
  [reactiveForm]="filtersForm"
  [formLayoutConfig]="filtersLayoutConfig"
  [formControlsConfigMap]="filtersConfigMap"
  [valuesMap]="valuesMap"
  [nonRemovableSectionIds]="['dateRange']"
  (updateValuesMap)="updateValuesMap($event)"
  (onApply)="applyFilters()"
  (onClear)="clearFilters()"
>
</top-bar-filters>

@if (error) {
  <hub-alert class="sb__error" alertType="danger">{{ error }}</hub-alert>
}

@if (loading) {
  <div class="loading__message d-flex align-items-center">
    <mat-spinner [diameter]="20"></mat-spinner>
    <span class="loading__message-text ml-3">Loading real-time data, please wait…</span>
  </div>
}

@if (summary && !loading) {
  @if (noData) {
    <div class="sb__card sb__no-data">
      <mat-icon class="sb__no-data-icon">search_off</mat-icon>
      <p class="sb__no-data-title">No data available for the selected filters.</p>
      <p class="sb__no-data-subtitle">Try adjusting or clearing the filters to see results.</p>
    </div>
  } @else {
    <div class="sb__date-basis-note">
      <mat-icon
        class="sb__date-basis-icon"
        matTooltip="The Time Range filter matches reviews and KPIs by the date the customer submitted their SmileBack rating (or last activity, for surveys still awaiting a rating) — not by the ticket's creation date. 'Total tickets' in the reviews card is a separate count of tickets created in this range, so its total may not match the number of reviews shown."
        matTooltipPosition="above"
        >info</mat-icon
      >
      <span>Filtered by survey response date, not ticket creation date</span>
    </div>

    <div class="sb__top-row">
      <div class="sb__kpi-card">
        <span class="sb__kpi-label-row">
          <span class="sb__kpi-label">Net CSAT Score</span>
          <mat-icon
            class="sb__kpi-info-icon"
            matTooltip="Percentage of rated reviews that were positive. Calculated as: positive reviews ÷ total reviews rated."
            matTooltipPosition="above"
            >info</mat-icon
          >
        </span>
        <div class="sb__gauge-wrapper">
          <canvas
            baseChart
            [type]="'doughnut'"
            [data]="gaugeChartData"
            [options]="gaugeOptions"
            [plugins]="gaugeChartPlugins"
          ></canvas>
        </div>
      </div>

      <div class="sb__reviews-card">
        <div class="sb__reviews-summary">
          <span class="sb__kpi-label-row">
            <span class="sb__kpi-label">SmileBack Reviews</span>
            <mat-icon
              class="sb__kpi-info-icon"
              matTooltip="Total number of reviews customers rated in this date range (Positive + Neutral + Negative below). 'Total tickets' is a separate count of all tickets created in this date range, whether or not they received a survey."
              matTooltipPosition="above"
              >info</mat-icon
            >
          </span>
          <span class="sb__kpi-value sb__kpi-value--brand">{{ summary.totalReviews | number }}</span>
          <span class="sb__kpi-subtext">{{ summary.totalTickets | number }} total tickets</span>
        </div>
        <div class="sb__emoji-row">
          <div class="sb__emoji-card sb__emoji-card--positive">
            <mat-icon class="sb__emoji-icon sb__emoji-icon--positive">sentiment_satisfied</mat-icon>
            <div class="sb__emoji-stats">
              <span class="sb__emoji-count sb__emoji-count--positive">{{ summary.positiveCount | number }}</span>
              <span class="sb__emoji-pct sb__emoji-pct--positive">{{ summary.positivePct }}%</span>
            </div>
          </div>
          <div class="sb__emoji-card sb__emoji-card--neutral">
            <mat-icon class="sb__emoji-icon sb__emoji-icon--neutral">sentiment_neutral</mat-icon>
            <div class="sb__emoji-stats">
              <span class="sb__emoji-count sb__emoji-count--neutral">{{ summary.neutralCount | number }}</span>
              <span class="sb__emoji-pct sb__emoji-pct--neutral">{{ summary.neutralPct }}%</span>
            </div>
          </div>
          <div class="sb__emoji-card sb__emoji-card--negative">
            <mat-icon class="sb__emoji-icon sb__emoji-icon--negative">sentiment_dissatisfied</mat-icon>
            <div class="sb__emoji-stats">
              <span class="sb__emoji-count sb__emoji-count--negative">{{ summary.negativeCount | number }}</span>
              <span class="sb__emoji-pct sb__emoji-pct--negative">{{ summary.negativePct }}%</span>
            </div>
          </div>
        </div>
      </div>

      <div class="sb__kpi-card">
        <span class="sb__kpi-label-row">
          <span class="sb__kpi-label">Response Rate</span>
          <mat-icon
            class="sb__kpi-info-icon"
            matTooltip="Percentage of tickets in this date range that received a rated review. Calculated as: reviews rated ÷ tickets created."
            matTooltipPosition="above"
            >info</mat-icon
          >
        </span>
        <span class="sb__kpi-value">{{ summary.responseRate | number: '1.2-2' }}%</span>
      </div>

      <div class="sb__kpi-card">
        <span class="sb__kpi-label-row">
          <span class="sb__kpi-label">Surveys Sent</span>
          <mat-icon
            class="sb__kpi-info-icon"
            matTooltip="Total surveys sent to customers in this period, including ones not yet rated. Not used in Net CSAT Score or Response Rate."
            matTooltipPosition="above"
            >info</mat-icon
          >
        </span>
        <span class="sb__kpi-value">{{ summary.totalSurveyed | number }}</span>
      </div>
    </div>

    <div class="sb__card sb__chart-card">
      <div class="sb__card-header">
        <div class="sb__legend">
          <div class="sb__legend-item">
            <span class="sb__legend-swatch" style="background: #00c100"></span>
            <span class="sb__legend-label">Positive</span>
          </div>
          <div class="sb__legend-item">
            <span class="sb__legend-swatch" style="background: #d8b706"></span>
            <span class="sb__legend-label">Neutral</span>
          </div>
          <div class="sb__legend-item">
            <span class="sb__legend-swatch" style="background: #bf360c"></span>
            <span class="sb__legend-label">Negative</span>
          </div>
          <div class="sb__legend-item">
            <span class="sb__legend-line"><span class="sb__legend-dot"></span></span>
            <span class="sb__legend-label">CSAT Rating</span>
          </div>
        </div>
        <span class="sb__chart-subtitle">By Month</span>
      </div>
      <div class="sb__chart-wrapper">
        <canvas
          baseChart
          [type]="'bar'"
          [data]="csatBarChartData"
          [options]="csatBarOptions"
          [plugins]="csatChartPlugins"
        ></canvas>
      </div>
    </div>

    <div class="sb__card sb__table-card">
      @if (reviewsError) {
        <hub-alert class="sb__error" alertType="danger">{{ reviewsError }}</hub-alert>
      }
      @if (loadingReviews) {
        <div class="d-flex justify-content-center align-items-center" style="min-height: 80px">
          <mat-spinner [diameter]="28"></mat-spinner>
        </div>
      } @else if (reviewsPage) {
        <div class="sb__table-scroll">
          <table
            mat-table
            [dataSource]="reviewsPage.data"
            class="udt-table w-100"
            matSort
            matSortDisableClear
            [matSortActive]="sortBy"
            [matSortDirection]="sortDir"
            (matSortChange)="onSortChange($event)"
          >
            <ng-container matColumnDef="ticketId">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Ticket #</th>
              <td mat-cell *matCellDef="let r">
                <a class="sb__ticket-link" [routerLink]="['/secured/managed-services/tickets', r.ticketId]">{{
                  r.ticketId
                }}</a>
              </td>
            </ng-container>
            <ng-container matColumnDef="dateRated">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Rated</th>
              <td mat-cell *matCellDef="let r">{{ r.dateRated | hubDate }}</td>
            </ng-container>
            <ng-container matColumnDef="closedDate">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Date Closed</th>
              <td mat-cell *matCellDef="let r">{{ r.closedDate ? (r.closedDate | hubDate) : '—' }}</td>
            </ng-container>
            <ng-container matColumnDef="rating">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Rating</th>
              <td mat-cell *matCellDef="let r">
                <span class="sb__rating" [ngClass]="ratingClass(r.rating)">{{ ratingLabel(r.rating) }}</span>
              </td>
            </ng-container>
            <ng-container matColumnDef="contactName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Contact</th>
              <td mat-cell *matCellDef="let r">{{ r.contactName ?? '—' }}</td>
            </ng-container>
            <ng-container matColumnDef="comment">
              <th mat-header-cell *matHeaderCellDef>Comment</th>
              <td mat-cell *matCellDef="let r" class="sb__comment-cell">{{ r.comment ?? '—' }}</td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let r; columns: displayedColumns"></tr>
          </table>
        </div>
        <mat-paginator
          [length]="reviewsPage.total"
          [pageSize]="pageSize"
          [pageIndex]="currentPage - 1"
          [pageSizeOptions]="[25, 50, 100, 200]"
          showFirstLastButtons
          (page)="onPageChange($event)"
        ></mat-paginator>
      }
    </div>
  }
}
